import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import {
  closeLiveSession, getLiveSession, listLiveDownloads, liveSocketUrl, liveTicket, openLiveSession, openProfileLiveSession,
  saveLiveDownload, uploadLiveFile, MAX_LIVE_UPLOAD_BYTES, type LiveDownload, type LiveStatus,
} from '@/api/playLive';

// The account's browser on the VPS, drawn from JPEG frames the play-live
// service streams through the worker's relay, driven with the mouse and
// keyboard over the same socket. Coordinates go out in the page's CSS pixels.

interface Tab { url: string; title: string }
interface PageState { tabs: Tab[]; active: number }
// A site's file input waiting for files; the service keeps the native dialog shut.
interface Chooser { id: string; multiple: boolean; accept: string | null }

const STATUS_CLS: Record<LiveStatus, string> = {
  requested: 'bg-surface-muted text-content-muted',
  starting: 'bg-amber-500/15 text-amber-400',
  live: 'bg-green-500/15 text-green-400',
  closed: 'bg-surface-muted text-content-muted',
};

const MOUSE_BUTTON = ['left', 'middle', 'right'] as const;

// Alt=1, Ctrl=2, Meta=4, Shift=8, as CDP counts them. Cmd on a Mac is sent as
// Ctrl: the remote browser runs on Linux, where Ctrl is what selects and undoes.
function modifiers(e: { altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }): number {
  return (e.altKey ? 1 : 0) | (e.ctrlKey || e.metaKey ? 2 : 0) | (e.shiftKey ? 8 : 0);
}

function fileSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1048576) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1048576).toFixed(1)} MB`;
}

// A site opened its file dialog. Files from this computer are uploaded to the
// worker and attached at once; files already in the session (uploaded before,
// or downloaded from another site) can be picked instead.
function FilePrompt({ sessionId, chooser, files, onAttach, notice }: {
  sessionId: string;
  chooser: Chooser;
  files: LiveDownload[];
  onAttach: (ids: string[]) => void;
  notice: (text: string) => void;
}) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [uploading, setUploading] = useState<string | null>(null);
  const [over, setOver] = useState(false);

  const upload = async (list: File[]) => {
    const chosen = chooser.multiple ? list : list.slice(0, 1);
    if (!chosen.length || uploading) return;
    const big = chosen.find((f) => f.size > MAX_LIVE_UPLOAD_BYTES);
    if (big) { notice(`${big.name} is over ${MAX_LIVE_UPLOAD_BYTES / 1048576} MB`); return; }
    const ids: string[] = [];
    try {
      for (const [i, f] of chosen.entries()) {
        setUploading(chosen.length > 1 ? `Uploading ${i + 1} of ${chosen.length}…` : `Uploading ${f.name}…`);
        ids.push((await uploadLiveFile(sessionId, f)).id);
      }
      onAttach([...picked, ...ids]);
    } catch (e: any) {
      notice(e?.message ?? 'Could not upload the file');
    } finally {
      setUploading(null);
      qc.invalidateQueries({ queryKey: ['live-downloads', sessionId] });
    }
  };

  const toggle = (id: string) => {
    if (!chooser.multiple) { onAttach([id]); return; }
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  };

  return (
    <div
      className={`absolute top-3 left-1/2 -translate-x-1/2 w-[min(440px,calc(100%-24px))] bg-surface border rounded-lg shadow-xl p-4 flex flex-col gap-3 ${over ? 'border-primary' : 'border-border'}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); upload([...e.dataTransfer.files]); }}
    >
      <div>
        <div className="text-sm font-medium text-content">The site asks for {chooser.multiple ? 'files' : 'a file'}</div>
        {chooser.accept && <div className="text-xs text-content-muted mt-0.5 break-all">Accepts {chooser.accept}</div>}
      </div>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        multiple={chooser.multiple}
        accept={chooser.accept ?? undefined}
        onChange={(e) => { const list = [...(e.target.files ?? [])]; e.target.value = ''; upload(list); }}
      />
      <Button size="sm" loading={!!uploading} onClick={() => inputRef.current?.click()}>
        {uploading ?? 'Choose on this computer…'}
      </Button>
      <div className="text-xs text-content-muted -mt-1 text-center">or drop {chooser.multiple ? 'files' : 'a file'} here</div>
      {files.length > 0 && (
        <div className="flex flex-col gap-1">
          <div className="text-xs text-content-muted">Or a file of this session:</div>
          <ul className="max-h-40 overflow-y-auto divide-y divide-border border border-border rounded-md">
            {files.map((f) => (
              <li key={f.id}>
                <button
                  className={`w-full flex items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-surface-muted ${picked.includes(f.id) ? 'bg-primary/10' : ''}`}
                  disabled={!!uploading}
                  onClick={() => toggle(f.id)}
                >
                  {chooser.multiple && <input type="checkbox" readOnly checked={picked.includes(f.id)} className="pointer-events-none" />}
                  <span className="truncate text-content" title={f.name}>{f.name}</span>
                  <span className="ml-auto text-xs text-content-muted whitespace-nowrap">
                    {f.direction === 'up' ? 'uploaded' : 'downloaded'} · {fileSize(f.size)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" disabled={!!uploading} onClick={() => onAttach([])}>Cancel</Button>
        {chooser.multiple && picked.length > 0 && (
          <Button size="sm" disabled={!!uploading} onClick={() => onAttach(picked)}>Attach {picked.length}</Button>
        )}
      </div>
    </div>
  );
}

function keyText(e: KeyboardEvent | React.KeyboardEvent): string | undefined {
  if (e.key === 'Enter') return '\r';
  const altGr = e.getModifierState?.('AltGraph');
  if (e.key.length === 1 && (altGr || (!e.ctrlKey && !e.metaKey && !e.altKey))) return e.key;
  return undefined;
}

export function LiveBrowser() {
  const { sessionId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const metaRef = useRef<{ w: number; h: number } | null>(null);
  const [connected, setConnected] = useState(false);
  const [runnerUp, setRunnerUp] = useState(false);
  const [hasFrame, setHasFrame] = useState(false);
  const [page, setPage] = useState<PageState>({ tabs: [], active: -1 });
  const [address, setAddress] = useState('');
  const [editingAddress, setEditingAddress] = useState(false);
  const [notices, setNotices] = useState<{ id: number; text: string }[]>([]);
  const [focused, setFocused] = useState(false);
  const [chooser, setChooser] = useState<Chooser | null>(null);

  const sessionQ = useQuery({
    queryKey: ['live-session', sessionId],
    queryFn: () => getLiveSession(sessionId),
    refetchInterval: (q) => (q.state.data?.status === 'closed' ? false : 3000),
  });
  const session = sessionQ.data;
  const downloadsQ = useQuery({
    queryKey: ['live-downloads', sessionId],
    queryFn: () => listLiveDownloads(sessionId),
  });
  const downloads = downloadsQ.data ?? [];
  const [saving, setSaving] = useState<string | null>(null);
  const active = session?.status === 'starting' || session?.status === 'live';

  const notice = useCallback((text: string) => {
    const id = Date.now() + Math.random();
    setNotices((n) => [...n.slice(-3), { id, text }]);
    setTimeout(() => setNotices((n) => n.filter((x) => x.id !== id)), 6000);
  }, []);

  const send = useCallback((msg: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  // ---------- socket ----------

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let drawing = false;

    const draw = async (data: ArrayBuffer) => {
      drawing = true;
      try {
        const bmp = await createImageBitmap(new Blob([data], { type: 'image/jpeg' }));
        const canvas = canvasRef.current;
        if (canvas) {
          if (canvas.width !== bmp.width || canvas.height !== bmp.height) {
            canvas.width = bmp.width;
            canvas.height = bmp.height;
          }
          canvas.getContext('2d')?.drawImage(bmp, 0, 0);
          setHasFrame(true);
        }
        bmp.close();
      } catch { /* a broken frame is skipped */ }
      drawing = false;
      // The service sends the next frame only after this.
      send({ type: 'ack' });
    };

    const connect = async () => {
      let ticket: string;
      try {
        ({ ticket } = await liveTicket(sessionId));
      } catch {
        if (!cancelled) retry = setTimeout(connect, 3000);
        return;
      }
      if (cancelled) return;
      const ws = new WebSocket(liveSocketUrl(sessionId, ticket));
      ws.binaryType = 'arraybuffer';
      wsRef.current = ws;
      ws.onopen = () => setConnected(true);
      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string') {
          if (!drawing) draw(ev.data as ArrayBuffer);
          else send({ type: 'ack' });
          return;
        }
        let msg: any;
        try { msg = JSON.parse(ev.data); } catch { return; }
        switch (msg.type) {
          case 'hello': setRunnerUp(!!msg.runner); break;
          case 'runner': setRunnerUp(!!msg.up); break;
          case 'meta': metaRef.current = { w: msg.w, h: msg.h }; break;
          case 'state': setPage({ tabs: msg.tabs ?? [], active: msg.active ?? -1 }); break;
          case 'notice': notice(msg.text); break;
          case 'filechooser':
            setChooser(msg.id ? { id: msg.id, multiple: !!msg.multiple, accept: msg.accept ?? null } : null);
            break;
          case 'download':
            notice(`Downloaded ${msg.file?.name ?? 'a file'} — see Files below`);
            qc.invalidateQueries({ queryKey: ['live-downloads', sessionId] });
            break;
          case 'clipboard':
            navigator.clipboard?.writeText(msg.text ?? '')
              .then(() => notice(msg.text ? `Copied ${msg.text.length} characters` : 'Nothing is selected'))
              .catch(() => notice('The browser did not allow writing to the clipboard'));
            break;
          case 'closed':
            qc.invalidateQueries({ queryKey: ['live-session', sessionId] });
            break;
        }
      };
      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        setConnected(false);
        // The service says again on reconnect if the site is still waiting.
        setChooser(null);
        if (!cancelled) {
          qc.invalidateQueries({ queryKey: ['live-session', sessionId] });
          retry = setTimeout(connect, 2000);
        }
      };
    };
    connect();
    return () => {
      cancelled = true;
      clearTimeout(retry);
      wsRef.current?.close();
      wsRef.current = null;
      setConnected(false);
    };
  }, [active, sessionId, send, notice, qc]);

  // The address bar follows the active tab unless someone is typing in it.
  const activeTab = page.tabs[page.active];
  useEffect(() => {
    if (!editingAddress) setAddress(activeTab?.url ?? '');
  }, [activeTab?.url, editingAddress]);

  // ---------- input ----------

  const toPage = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    const m = metaRef.current ?? { w: canvas.width, h: canvas.height };
    return {
      x: Math.round(((clientX - r.left) / r.width) * m.w * 10) / 10,
      y: Math.round(((clientY - r.top) / r.height) * m.h * 10) / 10,
    };
  }, []);

  // Moves are coalesced to one per animation frame.
  const pendingMove = useRef<Record<string, unknown> | null>(null);
  const flushMove = useCallback(() => {
    const m = pendingMove.current;
    pendingMove.current = null;
    if (m) send(m);
  }, [send]);

  const onMouse = (action: 'down' | 'up' | 'move') => (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!hasFrame) return;
    const { x, y } = toPage(e.clientX, e.clientY);
    const msg = {
      type: 'mouse', action, x, y, button: MOUSE_BUTTON[e.button] ?? 'left',
      buttons: e.buttons, clicks: e.detail || 1, mods: modifiers(e),
    };
    if (action === 'move') {
      if (!pendingMove.current) requestAnimationFrame(flushMove);
      pendingMove.current = msg;
      return;
    }
    flushMove();
    if (action === 'down') {
      canvasRef.current?.focus();
      // A button let go outside the screen still has to be let go remotely.
      window.addEventListener('mouseup', (up) => {
        if (up.target === canvasRef.current) return;
        const r = canvasRef.current?.getBoundingClientRect();
        if (!r) return;
        const p = toPage(Math.min(r.right, Math.max(r.left, up.clientX)), Math.min(r.bottom, Math.max(r.top, up.clientY)));
        send({ ...msg, ...p, action: 'up', buttons: up.buttons });
      }, { once: true });
    }
    send(msg);
  };

  // Wheel needs a non-passive listener to keep the dashboard from scrolling.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const k = e.deltaMode === 1 ? 40 : e.deltaMode === 2 ? 800 : 1;
      const { x, y } = toPage(e.clientX, e.clientY);
      send({ type: 'wheel', x, y, dx: e.deltaX * k, dy: e.deltaY * k, mods: modifiers(e) });
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [toPage, send]);

  const onKey = (action: 'down' | 'up') => (e: React.KeyboardEvent<HTMLCanvasElement>) => {
    const ctrl = e.ctrlKey || e.metaKey;
    const k = e.key.toLowerCase();
    // Paste comes from this computer's clipboard (the paste event below), copy
    // goes back to it; the remote browser's own clipboard is unreachable.
    if (ctrl && k === 'v') return;
    if (ctrl && k === 'c') {
      e.preventDefault();
      if (action === 'down') send({ type: 'copy' });
      return;
    }
    if (['Control', 'Meta', 'Shift', 'Alt', 'AltGraph', 'CapsLock'].includes(e.key) && action === 'down' && e.repeat) return;
    e.preventDefault();
    send({
      type: 'key', action, key: e.key, code: e.code, keyCode: e.keyCode,
      text: action === 'down' ? keyText(e) : undefined, mods: modifiers(e),
    });
  };

  const onPaste = (e: React.ClipboardEvent<HTMLCanvasElement>) => {
    const text = e.clipboardData.getData('text/plain');
    if (text) { e.preventDefault(); send({ type: 'text', text }); }
  };

  const save = async (file: LiveDownload) => {
    setSaving(file.id);
    try {
      await saveLiveDownload(sessionId, file);
    } catch (e: any) {
      notice(e?.message ?? 'Could not fetch the file');
    } finally {
      setSaving(null);
    }
  };

  const attach = (ids: string[]) => {
    if (!chooser) return;
    send({ type: 'files', chooser: chooser.id, files: ids });
    setChooser(null);
  };

  const nav = (action: string, extra: Record<string, unknown> = {}) => send({ type: 'nav', action, ...extra });

  // ---------- session controls ----------

  const closeMut = useMutation({
    mutationFn: () => closeLiveSession(sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['live-session', sessionId] }),
  });
  const reopenMut = useMutation({
    mutationFn: () => (session!.profile_id ? openProfileLiveSession(session!.profile_id) : openLiveSession(session!.account_id!)),
    onSuccess: (s) => navigate(`/play/live/${s.id}`, { replace: true }),
    onError: (e: any) => notice(e?.message ?? 'Could not open the browser'),
  });

  if (sessionQ.isLoading) return <div className="p-6 text-content-muted">Loading…</div>;
  if (sessionQ.error || !session) {
    return <div className="p-6 text-red-400">{(sessionQ.error as Error)?.message ?? 'Session not found'}</div>;
  }

  const waiting = session.status === 'requested'
    ? 'Waiting for the live service on the VPS to pick this up…'
    : session.status === 'starting'
      ? `${(session.note ?? 'opening the profile').replace(/^./, (ch) => ch.toUpperCase())}…`
      : !connected ? 'Connecting…' : !runnerUp ? 'Waiting for the browser…' : !hasFrame ? 'Waiting for the first frame…' : null;

  return (
    <div className="p-4 flex flex-col gap-3 min-h-0">
      <div className="flex flex-wrap items-center gap-3">
        {session.profile_id
          ? <Link to="/browser-profiles" className="text-sm text-accent hover:underline">← Browser profiles</Link>
          : <Link to="/play/publish" className="text-sm text-accent hover:underline">← Console access</Link>}
        <h1 className="text-lg font-semibold text-content">
          {session.account_name}
          {session.google_email && <span className="ml-2 text-sm font-normal text-content-muted">{session.google_email}</span>}
        </h1>
        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[session.status]}`}>
          {session.status}
        </span>
        <span className="text-xs text-content-muted">opened by {session.admin_email ?? '?'}</span>
        <div className="ml-auto">
          {session.status === 'closed' ? (
            <Button size="sm" loading={reopenMut.isPending} onClick={() => reopenMut.mutate()}>Open again</Button>
          ) : (
            <Button size="sm" variant="danger" loading={closeMut.isPending} onClick={() => closeMut.mutate()}>
              Close browser
            </Button>
          )}
        </div>
      </div>

      {session.status === 'closed' ? (
        <div className="bg-surface border border-border rounded-lg p-6 text-sm text-content-muted">
          The browser is closed{session.close_reason ? ` — ${session.close_reason}` : ''}.
          {session.profile_id ? 'The runner can check this profile again.' : 'The publishing runner can use this account again.'}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1 bg-surface border border-border rounded-lg px-2 py-1.5 min-h-[38px]">
            {page.tabs.map((t, i) => (
              <span
                key={i}
                className={`group flex items-center max-w-[220px] rounded-md text-xs ${i === page.active ? 'bg-primary/15 text-content' : 'text-content-muted hover:bg-surface-muted'}`}
              >
                <button className="truncate px-2 py-1" title={t.url} onClick={() => nav('tab', { index: i })}>
                  {t.title || t.url || 'New tab'}
                </button>
                {page.tabs.length > 1 && (
                  <button className="px-1.5 py-1 opacity-60 hover:opacity-100" title="Close tab" onClick={() => nav('closetab', { index: i })}>×</button>
                )}
              </span>
            ))}
            <button className="px-2 py-1 text-content-muted hover:text-content text-sm" title="New tab" onClick={() => nav('newtab')}>+</button>
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" title="Back" onClick={() => nav('back')}>←</Button>
            <Button size="sm" variant="secondary" title="Forward" onClick={() => nav('forward')}>→</Button>
            <Button size="sm" variant="secondary" title="Reload" onClick={() => nav('reload')}>⟳</Button>
            <input
              className="flex-1 px-3 py-1.5 rounded-md bg-background border border-border text-content text-sm font-mono"
              value={address}
              spellCheck={false}
              onFocus={(e) => { setEditingAddress(true); e.currentTarget.select(); }}
              onBlur={() => setEditingAddress(false)}
              onChange={(e) => setAddress(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { nav('goto', { url: address }); e.currentTarget.blur(); }
                if (e.key === 'Escape') e.currentTarget.blur();
              }}
              placeholder="Address or search"
            />
          </div>

          <div className="relative">
            <canvas
              ref={canvasRef}
              tabIndex={0}
              width={1600}
              height={900}
              className={`block w-full h-auto bg-black rounded-md border outline-none ${focused ? 'border-primary' : 'border-border'} ${hasFrame ? 'cursor-default' : 'opacity-40'}`}
              onMouseDown={onMouse('down')}
              onMouseUp={onMouse('up')}
              onMouseMove={onMouse('move')}
              onContextMenu={(e) => e.preventDefault()}
              onKeyDown={onKey('down')}
              onKeyUp={onKey('up')}
              onPaste={onPaste}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            />
            {waiting && (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-content-muted pointer-events-none">
                {waiting}
              </div>
            )}
            {chooser && (
              <FilePrompt sessionId={sessionId} chooser={chooser} files={downloads} onAttach={attach} notice={notice} />
            )}
            {notices.length > 0 && (
              <div className="absolute bottom-3 left-3 flex flex-col gap-1 pointer-events-none">
                {notices.map((n) => (
                  <span key={n.id} className="px-2 py-1 rounded bg-black/75 text-white text-xs max-w-xl">{n.text}</span>
                ))}
              </div>
            )}
          </div>
          <p className="text-xs text-content-muted">
            Click the screen to type into it. Ctrl/⌘+V pastes from this computer, Ctrl/⌘+C copies the selection back,
            files a site downloads show up under Files, and when a site asks for a file you pick it here.
            While this browser is open the runner leaves the account alone; it closes by itself after 5 minutes with
            nobody watching or 30 minutes without input.
          </p>
        </>
      )}

      {downloads.length > 0 && (
        <div className="bg-surface border border-border rounded-lg">
          <div className="px-3 py-2 border-b border-border text-sm font-medium text-content">
            Files <span className="text-xs font-normal text-content-muted">· what sites saved in this browser and what was uploaded to them, kept 7 days</span>
          </div>
          <ul className="divide-y divide-border">
            {downloads.map((f) => (
              <li key={f.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <span className="truncate text-content" title={f.name}>{f.name}</span>
                <span className="text-xs text-content-muted whitespace-nowrap">
                  {f.direction === 'up' ? '↑ uploaded' : '↓ downloaded'} · {fileSize(f.size)}
                </span>
                <span className="text-xs text-content-muted whitespace-nowrap">
                  {new Date(f.created_at * 1000).toLocaleString()}
                </span>
                <Button size="sm" variant="secondary" className="ml-auto" loading={saving === f.id} onClick={() => save(f)}>
                  Save
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
